<script setup>
import { computed, ref, onMounted, onUnmounted } from 'vue';
import Toast from 'dashboard/components-next/toast/Toast.vue';
import { emitter } from 'shared/helpers/mitt';
import { useI18n } from 'vue-i18n';

const props = defineProps({
  duration: {
    type: Number,
    default: 10000,
  },
  position: {
    type: String,
    default: 'bottom-left',
  },
});

const { t } = useI18n();

const snackMessages = ref([]);
const timeoutMap = new Map();

const visibleSnackMessages = computed(() =>
  snackMessages.value.slice(-3).reverse()
);

const stackClass = index => {
  if (index === 0) return 'z-30';

  if (index === 1) {
    return 'z-20 translate-y-12 scale-[0.98] ltr:translate-x-2 rtl:-translate-x-2 group-hover:translate-y-0 group-hover:scale-100 group-hover:ltr:translate-x-0 group-hover:rtl:translate-x-0 [&>*]:!pointer-events-none group-hover:[&>*]:!pointer-events-auto';
  }

  return 'z-10 translate-y-24 scale-[0.96] ltr:translate-x-4 rtl:-translate-x-4 group-hover:translate-y-0 group-hover:scale-100 group-hover:ltr:translate-x-0 group-hover:rtl:translate-x-0 [&>*]:!pointer-events-none group-hover:[&>*]:!pointer-events-auto';
};

const removeToast = key => {
  const timeout = timeoutMap.get(key);
  if (timeout) {
    clearTimeout(timeout);
    timeoutMap.delete(key);
  }

  snackMessages.value = snackMessages.value.filter(item => item.key !== key);
};

const onNewToastMessage = ({ message: originalMessage, action }) => {
  const message = action?.usei18n ? t(originalMessage) : originalMessage;
  const duration = action?.duration || props.duration;
  const key = Date.now() + Math.random();

  const nextToast = {
    key,
    message,
    action,
  };

  snackMessages.value.push(nextToast);
  timeoutMap.set(
    key,
    window.setTimeout(() => {
      removeToast(key);
    }, duration)
  );
};

onMounted(() => {
  emitter.on('newToastMessage', onNewToastMessage);
});

onUnmounted(() => {
  emitter.off('newToastMessage', onNewToastMessage);
  timeoutMap.forEach(timeout => clearTimeout(timeout));
  timeoutMap.clear();
});
</script>

<template>
  <div
    class="pointer-events-none fixed bottom-4 z-50 flex max-w-[calc(100vw-2rem)] flex-col items-start gap-3 bg-transparent"
    :class="
      position === 'bottom-center' ? 'left-1/2 -translate-x-1/2' : 'left-4'
    "
  >
    <transition-group
      name="toast-fade"
      tag="div"
      class="group flex flex-col-reverse items-start gap-3"
    >
      <div
        v-for="(snackMessage, index) in visibleSnackMessages"
        :key="snackMessage.key"
        class="relative origin-bottom-left transition-transform duration-200 ease-out"
        :class="stackClass(index)"
      >
        <Toast
          :message="snackMessage.message"
          :action="snackMessage.action"
          @close="removeToast(snackMessage.key)"
        />
      </div>
    </transition-group>
  </div>
</template>
